import type { MiniGamesConfig } from '../config/minigames.config';
import { CraneGame } from './CraneGame';
import { HammerGame } from './HammerGame';
import { MiniGameKind, MiniGamePhase, type MiniGame, type MiniGameAttempt, type MiniGameOutcome } from './MiniGame';
import { MixerGame } from './MixerGame';

/** What the golden bottle can start: one of the mini games or the quiz. */
export type GoldenChallenge = MiniGameKind | 'QUIZ';

/**
 * Shuffle bag for the golden bottle (user 2026-10-04: "wirklich Abwechslung … diese verschiedenen Minispiele
 * durchläuft und nicht einmal Kranspiel, dann Frage, dann wieder Kranspiel"): every available challenge comes once in a
 * random order before any comes again; a new round is shuffled anew and never starts with the one just played. Where
 * a run starts is random. The quiz is only in the bag while its question pool is loaded.
 */
export class ChallengeBag {
  private rest: GoldenChallenge[] = [];
  private last: GoldenChallenge | null = null;

  constructor(private readonly random: () => number) {}

  /** The next challenge out of the bag (refilled and shuffled when empty), or null with nothing available. */
  next(available: readonly GoldenChallenge[]): GoldenChallenge | null {
    if (available.length === 0) return null;
    // what is no longer available (e.g. the quiz pool failed) drops out of the current round
    this.rest = this.rest.filter((c) => available.includes(c));
    if (this.rest.length === 0) {
      this.rest = shuffle([...available], this.random);
      // the round boundary: never the same twice in a row (the bag is drawn from its end)
      if (this.rest.length > 1 && this.rest[this.rest.length - 1] === this.last) {
        const swap = Math.floor(this.random() * (this.rest.length - 1));
        [this.rest[swap], this.rest[this.rest.length - 1]] = [this.rest[this.rest.length - 1]!, this.rest[swap]!];
      }
    }
    this.last = this.rest.pop()!;
    return this.last;
  }

  /** A new run: a fresh, random start. */
  reset(): void {
    this.rest = [];
    this.last = null;
  }
}

/** Fisher–Yates with the given random source. */
function shuffle<T>(items: T[], random: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

export function createMiniGame(kind: MiniGameKind, config: MiniGamesConfig, round: number, random: () => number): MiniGame {
  switch (kind) {
    case MiniGameKind.Crane:
      return new CraneGame(config.crane, round, random);
    case MiniGameKind.Hammer:
      return new HammerGame(config.hammer, round, random);
    case MiniGameKind.Mixer:
      return new MixerGame(config.mixer, round, random);
  }
}

/**
 * One mini game at the golden bottle, engine- and DOM-free like QuizSession: playing, then the result for a moment.
 * `update` returns the outcome once, when the result phase is over and the run may go on.
 */
export class MiniGameSession {
  private phaseNow: MiniGamePhase = MiniGamePhase.Idle;
  private gameNow: MiniGame | null = null;
  private bonusStart = 0;
  private resultTime = 0;
  private result: MiniGameOutcome | null = null;

  constructor(private readonly config: MiniGamesConfig) {}

  get phase(): MiniGamePhase {
    return this.phaseNow;
  }

  get active(): boolean {
    return this.phaseNow !== MiniGamePhase.Idle;
  }

  get game(): MiniGame | null {
    return this.gameNow;
  }

  /** Bottles the share reached so far would pay. */
  get bonus(): number {
    return bonusFor(this.gameNow?.share ?? 0, this.bonusStart);
  }

  get maxBonus(): number {
    return this.bonusStart;
  }

  /** Set once the game is over (the result phase is showing it). */
  get outcome(): MiniGameOutcome | null {
    return this.result;
  }

  start(kind: MiniGameKind, round: number, bonusStart: number, random: () => number): void {
    this.gameNow = createMiniGame(kind, this.config, round, random);
    this.bonusStart = bonusStart;
    this.phaseNow = MiniGamePhase.Playing;
    this.resultTime = 0;
    this.result = null;
  }

  tap(): void {
    if (this.phaseNow === MiniGamePhase.Playing) this.gameNow?.tap();
  }

  /**
   * Per rendered frame (real seconds). `attempt` receives what the game judged this frame; returns the outcome once,
   * when the result phase ends.
   */
  update(dt: number, attempt?: (attempt: MiniGameAttempt) => void): MiniGameOutcome | null {
    const game = this.gameNow;
    if (!game) return null;
    if (this.phaseNow === MiniGamePhase.Playing) {
      const judged = game.update(dt);
      if (judged) attempt?.(judged);
      if (game.finished) {
        this.result = { kind: game.kind, share: game.share, bonus: bonusFor(game.share, this.bonusStart) };
        this.phaseNow = MiniGamePhase.Result;
        this.resultTime = 0;
      }
      return null;
    }
    if (this.phaseNow === MiniGamePhase.Result) {
      // the game keeps animating behind the result (sparks settle, the drum turns)
      game.update(dt);
      this.resultTime += dt;
      if (this.resultTime >= this.config.resultSeconds) {
        this.phaseNow = MiniGamePhase.Idle;
        return this.result;
      }
    }
    return null;
  }

  reset(): void {
    this.phaseNow = MiniGamePhase.Idle;
    this.gameNow = null;
    this.result = null;
    this.resultTime = 0;
  }
}

/** Bottles for a share of the round's bonus, rounded down; the full bonus only for the full share. */
export function bonusFor(share: number, bonusStart: number): number {
  if (share >= 1 - 1e-9) return bonusStart;
  return Math.floor(Math.max(0, share) * bonusStart);
}
